'use client'

import React, { useState, useRef, useEffect } from 'react'
import Link from 'next/link'
import styles from './navbar.module.scss'

interface MenuItemProps {
  active: string | null
  item: string
  children?: React.ReactNode
  href: string
}

interface MenuProps {
  children: React.ReactNode
  theme?: 'light' | 'dark'
}

interface ProductItemProps {
  title: string
  description: string
  href: string
  src: string
}

interface HoveredLinkProps {
  children: React.ReactNode
  href: string
  [key: string]: any
}

export const MenuItem: React.FC<MenuItemProps> = ({
  active,
  item,
  children,
  href,
}) => {
  const [isAnimating, setIsAnimating] = useState(false)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)


  const handleMouseEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    setIsAnimating(true)
  }

  const handleMouseLeave = () => {
    setIsAnimating(false)
  }

  useEffect(() => {
    if (!isAnimating && active === item) {
      timeoutRef.current = setTimeout(() => {}, 150)
    }
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [isAnimating, active, item])

  return (
    <div
      className={styles.menuItem}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <a href={href} className={styles.menuText}>
        {item}
      </a>
      {active === item && children && (
        <div
          className={`${styles.dropdown} ${
            active === item ? styles.dropdownActive : ''
          }`}
        >
          <div className={styles.dropdownContent}>{children}</div>
        </div>
      )}
    </div>
  )
}

export const Menu: React.FC<MenuProps> = ({ children, theme = 'light' }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)

  const handleMouseLeave = () => {}

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen)
  }

  return (
    <div className={styles.navbarWrapper}>
      <nav
        className={`${styles.navbar} ${styles[theme]}`}
        onMouseLeave={handleMouseLeave}
      >
        <div className={styles.navbarContainer}>
          {/* Desktop Menu */}
          <div className={styles.desktopMenu}>{children}</div>

          {/* Mobile Menu Button */}
          <button
            className={styles.mobileMenuButton}
            onClick={toggleMobileMenu}
            aria-label="Toggle mobile menu"
          >
            <span
              className={`${styles.hamburger} ${
                isMobileMenuOpen ? styles.hamburgerOpen : ''
              }`}
            >
              <span></span>
              <span></span>
              <span></span>
            </span>
          </button>
        </div>

        {/* Mobile Menu */}
        <div
          className={`${styles.mobileMenu} ${
            isMobileMenuOpen ? styles.mobileMenuOpen : ''
          }`}
        >
          <div className={styles.mobileMenuContent}>{children}</div>
        </div>
      </nav>
    </div>
  )
}

export const ProductItem: React.FC<ProductItemProps> = ({
  title,
  description,
  href,
  src,
}) => {
  return (
    <Link href={href} className={styles.productItem}>
      <img
        src={src}
        width={140}
        height={70}
        alt={title}
        className={styles.productImage}
      />
      <div className={styles.productInfo}>
        <h4 className={styles.productTitle}>{title}</h4>
        <p className={styles.productDescription}>{description}</p>
      </div>
    </Link>
  )
}

export const HoveredLink: React.FC<HoveredLinkProps> = ({
  children,
  href,
  ...rest
}) => {
  return (
    <Link href={href} className={styles.hoveredLink} {...rest}>
      {children}
    </Link>
  )
}
